import { useMutation } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { 
  exportDashboardToExcel, 
  extractFilenameFromResponse, 
  downloadBlob,
  DashboardExportEndpoint 
} from '../api/dashboardExport.api';
import type { DashboardFilters } from '../types/dashboard.types';

interface DashboardExportParams {
  endpoint: DashboardExportEndpoint;
  filters?: DashboardFilters;
}

export const useDashboardExport = () => {
  const mutation = useMutation({
    mutationFn: async ({ endpoint, filters }: DashboardExportParams) => {
      const response = await exportDashboardToExcel(endpoint, filters);
      return { response, endpoint };
    },
    onSuccess: ({ response, endpoint }) => {
      try {
        // Create blob from response
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        // Extract filename from headers or use default
        const filename = extractFilenameFromResponse(response as { headers?: Record<string, string> }, endpoint);
        
        // Download the file
        downloadBlob(blob, filename);

        toast({
          title: "Descarga completada",
          description: `El archivo Excel se ha descargado exitosamente.`,
          variant: "default",
        });
      } catch (error) {
        console.error("Error al procesar descarga:", error);
        toast({
          title: "Error al procesar descarga",
          description: "El archivo se descargó pero ocurrió un error al procesarlo.",
          variant: "destructive",
        });
      }
    },
    onError: (error: unknown) => {
      console.error("Error al exportar dashboard:", error);
      
      let errorMessage = "Ocurrió un error al generar el archivo Excel.";
      
      // Type guard for axios error
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
      
      if (axiosError?.response?.status === 401) {
        errorMessage = "No tienes permisos para descargar este archivo.";
      } else if (axiosError?.response?.status === 404) {
        errorMessage = "El endpoint de exportación no está disponible.";
      } else if (axiosError?.response?.status && axiosError.response.status >= 500) {
        errorMessage = "Error del servidor. Intenta de nuevo más tarde.";
      } else if (axiosError?.response?.data?.message) {
        errorMessage = axiosError.response.data.message;
      }
      
      toast({
        title: "Error al exportar",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  return {
    exportToExcel: mutation.mutate,
    isExporting: mutation.isPending,
    error: mutation.error,
  };
};